// @ts-check
/** @typedef {import('../types/api').Product} Product */

import { ArrowDown, ArrowUp, Plus, RefreshCw, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminProductService from '../services/adminProductService';

const FeaturedProductsManager = () => {
  const toast = useToast();
  /** @type {[Product[], Function]} */
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add product search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef(null);
  const searchInputRef = useRef(null);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminProductService.getFeaturedProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load featured products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Focus search input when opened
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Debounced search — runs as soon as the panel opens (browsing the default top
  // products) and re-filters on every keystroke, rather than showing nothing until the
  // admin types something.
  useEffect(() => {
    if (!showSearch) return;

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const query = searchQuery.trim();
        const data = await adminProductService.getAllProducts({
          ...(query ? { search: query } : {}),
          limit: 10,
          status: 'published',
        });
        const items = data.items || data;
        // Filter out already-featured products
        const featuredIds = new Set(products.map((p) => p.id));
        setSearchResults(Array.isArray(items) ? items.filter((p) => !featuredIds.has(String(p.id))) : []);
      } catch (err) {
        console.error(err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery, showSearch, products]);

  const handleReorder = async (productId, direction) => {
    const idx = products.findIndex((p) => p.id === productId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= products.length) return;

    const updated = [...products];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updated.forEach((p, i) => (p.featured_order = i));
    setProducts(updated);

    try {
      await adminProductService.reorderFeaturedProducts(
        updated.map((p) => ({ id: p.id, featured_order: p.featured_order })),
      );
    } catch (err) {
      toast.error('Failed to reorder products');
      console.error(err);
      loadProducts();
    }
  };

  const handleRemove = async (productId) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    // Optimistic update
    const prev = [...products];
    setProducts(products.filter((p) => p.id !== productId));

    try {
      await adminProductService.toggleFeature(productId, false);
      toast.success(`Removed "${product.name}" from featured`);

      // Re-save order for remaining products
      const remaining = prev.filter((p) => p.id !== productId);
      if (remaining.length > 0) {
        await adminProductService.reorderFeaturedProducts(remaining.map((p, i) => ({ id: p.id, featured_order: i })));
      }
    } catch (err) {
      toast.error('Failed to remove product');
      console.error(err);
      setProducts(prev);
    }
  };

  const handleAdd = async (product) => {
    try {
      await adminProductService.toggleFeature(product.id, true);
      toast.success(`Added "${product.name}" to featured`);
      // Keep the query and results as-is — refreshing `products` re-runs the search
      // effect above and it naturally drops the just-added product via featuredIds,
      // instead of wiping the whole result list and forcing the admin to retype to add
      // another.
      await loadProducts();
    } catch (err) {
      toast.error('Failed to add product');
      console.error(err);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading featured products...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground/70">
          The first 6 products appear in the Outfit Inspiration slider on the homepage.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={loadProducts}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            onClick={() => {
              setShowSearch(!showSearch);
              setSearchQuery('');
              setSearchResults([]);
            }}
            className="flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-xs hover:bg-primary/90 transition"
          >
            <Plus size={14} />
            Add Product
          </button>
        </div>
      </div>

      {/* Search / Add Panel */}
      {showSearch && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products by name..."
              className="w-full pl-9 pr-9 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-foreground"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {searching && <p className="text-xs text-muted-foreground/70">Searching...</p>}

          {searchResults.length > 0 && (
            <div className="max-h-60 overflow-y-auto space-y-1">
              {searchResults.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleAdd(product)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition text-left"
                >
                  <div className="flex-shrink-0 w-10 h-13 bg-muted rounded-lg overflow-hidden">
                    {product.image_urls?.[0] ? (
                      <img src={product.image_urls[0]} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground/50 text-[9px]">
                        No img
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{product.name}</p>
                    <p className="text-xs text-muted-foreground">${product.price}</p>
                  </div>
                  <Plus size={16} className="flex-shrink-0 text-muted-foreground/70" />
                </button>
              ))}
            </div>
          )}

          {!searching && searchResults.length === 0 && (
            <p className="text-xs text-muted-foreground/70 text-center py-2">
              {searchQuery.trim() ? 'No matching products found.' : 'No products available to feature.'}
            </p>
          )}
        </div>
      )}

      {/* Featured Products List */}
      {products.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border border-border rounded-lg">
          No featured products yet. Click "Add Product" to get started.
        </div>
      ) : (
        <div className="space-y-2">
          {products.map((product, index) => (
            <div
              key={product.id}
              className={`flex items-center gap-4 p-3 border rounded-lg transition ${
                index < 6 ? 'bg-card border-border' : 'bg-muted/50 border-border/60 opacity-60'
              }`}
            >
              {/* Position Badge */}
              <span
                className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                  index < 6 ? 'bg-primary text-primary-foreground' : 'bg-accent text-muted-foreground'
                }`}
              >
                {index + 1}
              </span>

              {/* Thumbnail */}
              <div className="flex-shrink-0 w-12 h-16 bg-muted rounded-lg overflow-hidden">
                {product.image_urls?.[0] ? (
                  <img src={product.image_urls[0]} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground/50 text-xs">
                    No img
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{product.name}</p>
                <p className="text-xs text-muted-foreground">
                  {product.sale_price ? (
                    <>
                      <span className="text-red-500">${product.sale_price}</span>{' '}
                      <span className="line-through">${product.price}</span>
                    </>
                  ) : (
                    <span>${product.price}</span>
                  )}
                  {product.status !== 'published' && (
                    <span className="ml-2 text-amber-500 uppercase text-[10px] font-medium">{product.status}</span>
                  )}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => handleReorder(product.id, 'up')}
                    disabled={index === 0}
                    className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                    title="Move up"
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    onClick={() => handleReorder(product.id, 'down')}
                    disabled={index === products.length - 1}
                    className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                    title="Move down"
                  >
                    <ArrowDown size={16} />
                  </button>
                </div>
                <button
                  onClick={() => handleRemove(product.id)}
                  className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 text-red-500 transition"
                  title="Remove from featured"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeaturedProductsManager;
